import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export function usePlanBonuses(tenantId?: string) {
  const queryClient = useQueryClient();

  // Buscar bonificação ativa
  const { data: activeBonus, isLoading } = useQuery({
    queryKey: ['plan_bonus', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;

      const { data, error } = await supabase
        .from('tenant_plan_bonuses')
        .select(`
          *,
          bonus_plan:subscription_plans!bonus_plan_id(name),
          return_plan:subscription_plans!return_plan_id(name),
          original_plan:subscription_plans!original_plan_id(name)
        `)
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  // Aplicar bonificação
  const applyBonus = useMutation({
    mutationFn: async (params: {
      tenantId: string;
      bonusPlanId: string;
      returnPlanId: string;
      bonusPeriod: 'monthly' | 'annual' | 'lifetime';
      endDate: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc('apply_plan_bonus', {
        p_tenant_id: params.tenantId,
        p_bonus_plan_id: params.bonusPlanId,
        p_return_plan_id: params.returnPlanId,
        p_bonus_period: params.bonusPeriod,
        p_end_date: params.endDate,
        p_notes: params.notes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan_bonus'] });
      queryClient.invalidateQueries({ queryKey: ['admin_tenants'] });
      toast({
        title: 'Bonificação Aplicada',
        description: 'O plano foi atualizado com sucesso!',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao Aplicar Bonificação',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Reverter bonificação
  const revertBonus = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data, error } = await supabase.rpc('revert_plan_bonus', {
        p_tenant_id: tenantId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan_bonus'] });
      queryClient.invalidateQueries({ queryKey: ['admin_tenants'] });
      toast({
        title: 'Bonificação Revertida',
        description: 'O tenant retornou ao plano original.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao Reverter Bonificação',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    activeBonus,
    isLoading,
    applyBonus,
    revertBonus,
  };
}
